import React, { useEffect, useState } from 'react'
import { ColumnsType } from 'antd/lib/table'
import { useNavigate, useParams } from 'react-router-dom'
import { IconButton } from '../../shared/components/buttons/icon-button/IconButton'
import { IconArrowBack } from '../../shared/components/icons/IconArrowBack'
import { useAppSelector } from '../../app/store/hooks/Redux'
import { selectCompetitorsFilter } from '../advertising-research/model/competitorsSlice'
import { TTableResponse } from '../../shared/types/TableResponse.type'
import { BpTable } from '../../shared/components/cards/table/BpTable'
import { IBpPaginationProps } from '../../shared/components/cards/pagination/BpPagination'
import {
  selectResearchActiveTab,
  selectResearchFilters,
} from '../advertising-research/model/researchSlice'
import { selectKeywordsFilter } from '../advertising-research/model/keywordsSlice'
import { IKeywordsFilter } from '../advertising-research/model/types/KeywordsFilter.interface'
import { ICompetitorsFilter } from '../advertising-research/model/types/CompetitorsFilter.interface'
import { IResearchFilter } from '../advertising-research/model/types/ResearchFilter.interface'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../shared/lib/hooks/UseSorter'
import { ExportDrop } from '../../shared/components/cards/export-drop/ExportDrop'
import { useTranslation } from 'react-i18next'
import {
  GetResearchCompetitorsColumnApiArg,
  ResearchColumnArg,
  ResearchCompetitorsColumnItem,
  useExportResearchAds,
  useGetResearchCompetitorsColumnQuery,
} from '../../app/api'
import { AdCell } from '../../shared/ui/cells/AdCell'
import clsx from 'clsx'

import styles from './AdsPage.module.scss'

export const AdsPage = () => {
  const { t } = useTranslation()
  const { id, domain, keyword } = useParams()
  const navigate = useNavigate()
  const [countRows, setCountRows] = useState<number>(0)
  const activeTab = useAppSelector(selectResearchActiveTab)
  const competitorsFilter = useAppSelector<ICompetitorsFilter>(selectCompetitorsFilter)
  const keywordsFilter = useAppSelector<IKeywordsFilter>(selectKeywordsFilter)
  const { date } = (activeTab === 'Keywords' ? keywordsFilter : competitorsFilter).dateRange
  const { country, domain: domains } = useAppSelector<IResearchFilter>(selectResearchFilters)
  const [queryParams, setQueryParams] = useState<
    ResearchColumnArg<GetResearchCompetitorsColumnApiArg>
  >({
    page: 1,
    'per-page': 10,
    projectId: id ? +id : undefined,
    domain,
    'domain[]': !domain && domains ? domains.split(',') : undefined,
    date,
    'country_id[]': country,
    keyword,
  })

  const { data, isLoading, isFetching } = useGetResearchCompetitorsColumnQuery(
    queryParams as GetResearchCompetitorsColumnApiArg,
    { skip: id == undefined },
  )

  const [exportFile, { isFetching: exportAdsLoading }] = useExportResearchAds()

  useEffect(() => {
    if (!data?.data) return
    setCountRows(data.data.total)
  }, [data])

  const columns: ColumnsType<ResearchCompetitorsColumnItem> = [
    {
      title: t('table.columns.ad'),
      dataIndex: 'text',
      key: 'text',
      render: (_: string, record) => <AdCell {...record} />,
      width: 500,
    },
    {
      title: t('table.columns.keyword(s)'),
      dataIndex: 'keywords',
      key: 'keywords',
      width: 250,
    },
    {
      title: t('table.columns.firstFound'),
      dataIndex: 'first_found',
      key: 'first_found',
      width: 150,
    },
    {
      title: t('table.columns.lastFound'),
      dataIndex: 'last_found',
      key: 'last_found',
      width: 150,
      sorter: true,
    },
  ]

  const handleTableChange = (
    { current, pageSize }: IBpPaginationProps,
    sorter?:
      | SorterResult<ResearchCompetitorsColumnItem>
      | SorterResult<ResearchCompetitorsColumnItem>[],
  ) => {
    setQueryParams({ ...queryParams, page: current, 'per-page': pageSize, sort: useSorter(sorter) })
  }

  return (
    <section className={clsx(styles.wrapper)}>
      <div className={clsx(styles.header)}>
        <span className={clsx(styles.titleRow)}>
          <IconButton size='lg' onClick={() => navigate(-1)}>
            <IconArrowBack color='#00326D' />
          </IconButton>
          <h3 className={clsx(styles.title)}>
            {countRows} {t('pages.advertisingResearch.adsFor')} “{keyword && keyword}
            {domain && domain}”
          </h3>
        </span>
        <ExportDrop
          columns={[
            { label: t('table.columns.ad'), keys: ['text', 'title', 'finish_domain'] },
            { label: t('table.columns.keyword(s)'), keys: ['keywords'] },
            { label: t('table.columns.firstFound'), keys: ['first_found'] },
            { label: t('table.columns.lastFound'), keys: ['last_found'] },
          ]}
          loading={exportAdsLoading}
          onExport={(exportParams) => exportFile({ ...queryParams, ...exportParams })}
        />
      </div>
      <BpTable
        columns={columns}
        data={data as TTableResponse<ResearchCompetitorsColumnItem>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowKey={({ title, last_found }) => `${title}${last_found}`}
        scroll={{ x: '896px' }}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      ></BpTable>
    </section>
  )
}
